import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  Inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  sanitizeForSpeech,
  splitIntoSentences,
} from '../../utils/speech-text-sanitizer.util';
import { readTargetedLang, resolveBcp47Lang } from '../../utils/locale-bcp47.util';
import { logAssistantMessageLifecycle } from 'src/app/features/assistant/voice/utils/assistant-message-finalizer.util';

export interface VoiceSynthesisState {
  speaking: boolean;
  paused: boolean;
  error: string | null;
}

export interface SpeakOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
}

/** Bobo — friendly cartoon elephant: young boy energy on browser TTS. */
export const DEFAULT_ASSISTANT_SPEECH_OPTIONS: SpeakOptions = {
  rate: 1.05,
  pitch: 1.15,
  volume: 0.95,
};

const CHUNK_GAP_MS = 90;
const VOICE_CATALOG_DEFER_MS = 500;

/** Explicit child / boy voices when the OS provides them. */
const CHILD_MALE_VOICE_HINTS =
  /child|kid\b|boy\b|junior|young.*boy|youth.*male|male.*child|leo\b/i;

/** Young or light male voices — preferred base for pitching up slightly. */
const YOUNG_MALE_VOICE_HINTS =
  /google us english male|google uk english male|english male|\bmale\b(?!.*female)|\bman\b(?!.*woman)/i;

/** Neutral voices with no clear gender — deprioritized vs explicit male. */
const NEUTRAL_VOICE_HINTS =
  /google us english(?!.*male)|google uk english(?!.*male)|natural(?!.*male)(?!.*female)/i;

/** Deep adult male voices — too grown-up for Bobo. */
const DEEP_ADULT_MALE_HINTS =
  /baritone|deep\b|david|mark\b|george|richard|guy\b|microsoft david|microsoft guy|microsoft mark|daniel|fred|thomas|arthur|brian|matthew|paul\b|lee\b|andrew|james\b/i;

/** Female voices — wrong persona for child-male Bobo. */
const FEMALE_VOICE_HINTS =
  /female|woman|girl\b|zira|sonia|samantha|karen|veena|linda|susan|hazel|sarah|victoria|tessa|fiona|monica|serena|moira|sangeeta|priya|google uk english female|google us english female|microsoft zira|microsoft sonia|natural.*female|neural.*female|harmony|jenny|aria\b|emma\b|michelle|helen|catherine|lisa\b|nancy|allison|ava\b/i;

type CancelReason = 'new_speak' | 'explicit_cancel' | 'destroy';

@Injectable({
  providedIn: 'root',
})
export class VoiceSynthesisService implements OnDestroy {
  private readonly state = new BehaviorSubject<VoiceSynthesisState>({
    speaking: false,
    paused: false,
    error: null,
  });

  private voicesListener: (() => void) | null = null;

  /**
   * Chrome/Chromium often expose only a default voice until `voiceschanged` fires.
   * Used so the first auto-speak uses the same catalog as replay.
   */
  private browserVoicesCatalogReady = false;

  /** Clears deferred browser speak (timers + voiceschanged) when cancel/speak replaces. */
  private pendingBrowserSpeakDispose: (() => void) | null = null;

  /** Invalidates in-flight deferred speak() after cancel() or a newer speak(). */
  private speakGeneration = 0;

  /** Dev logging: last turn id passed to speakMessageContent. */
  private lastSpeakTurnId: number | undefined;

  /** Queued sentence chunks for sequential playback. */
  private chunkQueue: string[] = [];
  private chunkTimer: ReturnType<typeof setTimeout> | null = null;
  private chunkGeneration = 0;
  /** Keeps speaking=true across streamed sentence gaps until finishReplyPlayback(). */
  private replyPlaybackActive = false;
  /** One-shot speak() sets this; finishReplyPlayback runs when queue drains. */
  private replyPlaybackPendingFinish = false;
  private lastSpeakOpts: SpeakOptions | undefined;
  /** Cached voice per language for consistent multi-chunk playback. */
  private stickyVoiceByLang = new Map<string, SpeechSynthesisVoice>();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      const syn = window.speechSynthesis;
      if (syn) {
        const markReady = () => {
          this.browserVoicesCatalogReady = true;
          syn.removeEventListener('voiceschanged', markReady);
        };
        syn.addEventListener('voiceschanged', markReady);
        if (syn.getVoices().length > 0) {
          this.browserVoicesCatalogReady = true;
          syn.removeEventListener('voiceschanged', markReady);
        }
      }
    }
  }

  /** Warm voice catalog early (call from dashboard / app init). */
  preloadVoices(): void {
    if (!this.isSupported()) return;
    try {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        this.browserVoicesCatalogReady = true;
      }
    } catch {
      /* ignore */
    }
  }

  /** True when no utterance is playing, queue empty, and reply session ended. */
  isIdle(): boolean {
    return !this.hasPendingChunks() && !this.replyPlaybackActive;
  }

  /** Start a multi-chunk assistant reply (streaming or batched). */
  beginReplyPlayback(): void {
    this.replyPlaybackActive = true;
    this.replyPlaybackPendingFinish = false;
    this.state.next({ speaking: true, paused: false, error: null });
  }

  /** End reply session; speaking clears when queue drains. */
  finishReplyPlayback(): void {
    this.replyPlaybackPendingFinish = false;
    this.replyPlaybackActive = false;
    this.syncSpeakingState();
  }

  private hasPendingChunks(): boolean {
    return (
      this.chunkQueue.length > 0 ||
      this.chunkTimer !== null ||
      this.state.value.speaking
    );
  }

  private syncSpeakingState(): void {
    const speaking = this.hasPendingChunks() || this.replyPlaybackActive;
    if (this.state.value.speaking !== speaking) {
      this.state.next({
        speaking,
        paused: false,
        error: speaking ? null : this.state.value.error,
      });
    }
  }

  private maybeFinishReplyPlayback(): void {
    if (
      this.replyPlaybackPendingFinish &&
      !this.chunkQueue.length &&
      !this.chunkTimer
    ) {
      this.finishReplyPlayback();
    }
  }

  private clearPendingBrowserSpeak(): void {
    if (this.pendingBrowserSpeakDispose) {
      try {
        this.pendingBrowserSpeakDispose();
      } catch {
        /* ignore */
      }
      this.pendingBrowserSpeakDispose = null;
    }
  }

  private clearChunkQueue(): void {
    this.chunkGeneration += 1;
    this.chunkQueue = [];
    if (this.chunkTimer) {
      clearTimeout(this.chunkTimer);
      this.chunkTimer = null;
    }
  }

  isSupported(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  getState$() {
    return this.state.asObservable();
  }

  /** Collapse whitespace; ensure space after sentence end for clearer pauses in browser TTS. */
  prepareTextForTts(text: string): string {
    let t = text.replace(/\s+/g, ' ').trim();
    t = t.replace(/([.!?])([A-Za-zÀ-ÖØ-öø-ÿ])/g, '$1 $2');
    return t;
  }

  private resolveLang(explicit?: string): string {
    if (explicit) return resolveBcp47Lang(explicit);
    return resolveBcp47Lang(readTargetedLang());
  }

  private scoreVoice(name: string): number {
    const n = name.toLowerCase();
    let s = 0;
    if (CHILD_MALE_VOICE_HINTS.test(n)) s += 24;
    if (YOUNG_MALE_VOICE_HINTS.test(n) && !DEEP_ADULT_MALE_HINTS.test(n)) s += 14;
    if (DEEP_ADULT_MALE_HINTS.test(n)) s -= 18;
    if (FEMALE_VOICE_HINTS.test(n)) s -= 24;
    if (NEUTRAL_VOICE_HINTS.test(n)) s -= 8;
    return s;
  }

  private pickVoiceAndScore(lang: string): {
    voice: SpeechSynthesisVoice | null;
    score: number;
  } {
    if (!this.isSupported()) return { voice: null, score: -Infinity };
    const voices = window.speechSynthesis.getVoices();
    if (!voices?.length) return { voice: null, score: -Infinity };
    const langPrefix = lang.split('-')[0].toLowerCase();
    const inLang = voices.filter(v =>
      v.lang?.toLowerCase().startsWith(langPrefix)
    );
    const pool = inLang.length ? inLang : voices;

    let best: SpeechSynthesisVoice | null = null;
    let bestScore = -Infinity;
    for (const v of pool) {
      const name = `${v.name || ''} ${v.lang || ''}`;
      const sc = this.scoreVoice(name);
      if (sc > bestScore) {
        bestScore = sc;
        best = v;
      }
    }
    if (best && bestScore > -Infinity) return { voice: best, score: bestScore };
    const fallback = pool[0] ?? voices[0] ?? null;
    return {
      voice: fallback,
      score: fallback
        ? this.scoreVoice(`${fallback.name} ${fallback.lang}`)
        : -Infinity,
    };
  }

  private pickVoice(lang: string): SpeechSynthesisVoice | null {
    const cached = this.stickyVoiceByLang.get(lang);
    if (cached && this.isSupported()) {
      const stillAvailable = window.speechSynthesis
        .getVoices()
        .some(v => v.voiceURI === cached.voiceURI);
      if (stillAvailable) return cached;
      this.stickyVoiceByLang.delete(lang);
    }
    const picked = this.pickVoiceAndScore(lang).voice;
    if (picked) {
      this.stickyVoiceByLang.set(lang, picked);
    }
    return picked;
  }

  private shouldDeferBrowserSpeak(lang: string): boolean {
    if (!this.isSupported()) return false;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return true;
    if (!this.browserVoicesCatalogReady) return true;
    const { score } = this.pickVoiceAndScore(lang);
    if (score < 4 && voices.length < 8) return true;
    return false;
  }

  speak(text: string, opts?: SpeakOptions): void {
    if (!isPlatformBrowser(this.platformId) || !text?.trim()) return;
    const sanitized = sanitizeForSpeech(text, { maxSentences: 0 });
    const sentences = splitIntoSentences(this.prepareTextForTts(sanitized));
    if (!sentences.length) return;
    this.speakChunks(sentences, opts, true);
  }

  /**
   * Speak assistant message content (full reply or streamed sentence).
   * Only invalidatePendingSpeak / cancel() should stop active speech.
   */
  speakMessageContent(
    content: string,
    turnId?: number,
    opts?: SpeakOptions
  ): void {
    const trimmed = content?.trim();
    if (!isPlatformBrowser(this.platformId) || !trimmed) return;
    this.lastSpeakTurnId = turnId;
    this.devLog('speech start', {
      turnId,
      length: trimmed.length,
      preview: trimmed.slice(0, 80),
    });
    logAssistantMessageLifecycle('SPEECH', {
      turnId,
      contentLength: trimmed.length,
    });
    this.speakChunk(trimmed, opts);
  }

  /** Enqueue a single sentence chunk (streaming). */
  speakChunk(text: string, opts?: SpeakOptions): void {
    if (!isPlatformBrowser(this.platformId) || !text?.trim()) return;
    const sanitized = sanitizeForSpeech(text, { maxSentences: 0 });
    const prepared = this.prepareTextForTts(sanitized);
    if (!prepared) return;
    if (opts) this.lastSpeakOpts = opts;
    this.enqueueChunks([prepared], opts ?? this.lastSpeakOpts);
  }

  speakChunks(
    chunks: string[],
    opts?: SpeakOptions,
    pendingFinish = false
  ): void {
    if (!isPlatformBrowser(this.platformId) || !chunks.length) return;
    const prepared = chunks
      .map(c =>
        this.prepareTextForTts(
          sanitizeForSpeech(c, pendingFinish ? { maxSentences: 0 } : undefined)
        )
      )
      .filter(Boolean);
    if (!prepared.length) return;
    if (opts) this.lastSpeakOpts = opts;
    this.invalidatePendingSpeak('new_speak');
    this.replyPlaybackActive = true;
    this.replyPlaybackPendingFinish = pendingFinish;
    this.enqueueChunks(prepared, opts ?? this.lastSpeakOpts, true);
  }

  private enqueueChunks(
    chunks: string[],
    opts: SpeakOptions | undefined,
    replaceQueue = false
  ): void {
    if (replaceQueue) {
      this.clearChunkQueue();
    }
    this.chunkQueue.push(...chunks);
    if (this.replyPlaybackActive || !this.state.value.speaking) {
      this.drainChunkQueue(opts);
    }
  }

  private drainChunkQueue(opts?: SpeakOptions): void {
    if (!this.chunkQueue.length) {
      this.syncSpeakingState();
      this.maybeFinishReplyPlayback();
      return;
    }
    const next = this.chunkQueue.shift()!;
    const gen = this.chunkGeneration;
    this.speakSingleUtterance(next, opts, () => {
      if (gen !== this.chunkGeneration) return;
      if (this.chunkQueue.length > 0) {
        this.chunkTimer = setTimeout(() => {
          this.chunkTimer = null;
          if (gen === this.chunkGeneration) {
            this.drainChunkQueue(opts);
          }
        }, CHUNK_GAP_MS);
      } else {
        this.syncSpeakingState();
        this.maybeFinishReplyPlayback();
      }
    });
  }

  private speakSingleUtterance(
    prepared: string,
    opts: SpeakOptions | undefined,
    onDone: () => void
  ): void {
    const gen = this.speakGeneration;
    this.speakWithSpeechSynthesis(prepared, opts, gen, onDone);
  }

  private invalidatePendingSpeak(reason: CancelReason = 'new_speak'): void {
    this.speakGeneration += 1;
    this.replyPlaybackActive = false;
    this.replyPlaybackPendingFinish = false;
    this.clearPendingBrowserSpeak();
    this.clearChunkQueue();
    if (this.isSupported()) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    this.state.next({ speaking: false, paused: false, error: null });
    this.devLog('speech cancelled', { reason, turnId: this.lastSpeakTurnId });
  }

  private speakWithSpeechSynthesis(
    prepared: string,
    opts: SpeakOptions | undefined,
    generation: number,
    onUtteranceEnd?: () => void
  ): void {
    if (!this.isSupported()) return;

    const lang = this.resolveLang(opts?.lang);
    const rate = opts?.rate ?? DEFAULT_ASSISTANT_SPEECH_OPTIONS.rate ?? 0.95;
    const pitch = opts?.pitch ?? DEFAULT_ASSISTANT_SPEECH_OPTIONS.pitch ?? 1.1;
    const volume = opts?.volume ?? DEFAULT_ASSISTANT_SPEECH_OPTIONS.volume ?? 1;

    this.clearPendingBrowserSpeak();

    let spoken = false;
    const start = () => {
      if (spoken || generation !== this.speakGeneration) return;
      spoken = true;
      this.clearPendingBrowserSpeak();
      if (generation !== this.speakGeneration) return;

      const voice = this.pickVoice(lang);
      const u = new SpeechSynthesisUtterance(prepared);
      u.lang = lang;
      u.rate = rate;
      u.pitch = pitch;
      u.volume = volume;
      if (voice) u.voice = voice;

      this.devLog('utterance start', {
        voice: voice?.name ?? 'default',
        lang,
        length: prepared.length,
      });

      u.onstart = () =>
        this.state.next({ speaking: true, paused: false, error: null });
      u.onend = () => {
        if (generation !== this.speakGeneration) return;
        this.devLog('utterance end', { length: prepared.length });
        onUtteranceEnd?.();
        if (
          !this.replyPlaybackActive &&
          !this.chunkQueue.length &&
          !this.chunkTimer &&
          generation === this.speakGeneration
        ) {
          this.state.next({ speaking: false, paused: false, error: null });
        }
      };
      u.onerror = e => {
        if (generation !== this.speakGeneration) return;
        this.replyPlaybackActive = false;
        this.replyPlaybackPendingFinish = false;
        this.devLog('utterance error', { error: e.error });
        this.state.next({
          speaking: false,
          paused: false,
          error: e.error || 'speech-synthesis-error',
        });
        onUtteranceEnd?.();
      };

      window.speechSynthesis.speak(u);
    };

    if (!this.shouldDeferBrowserSpeak(lang)) {
      start();
      return;
    }

    const syn = window.speechSynthesis;
    let timeoutId = 0;
    let microId = 0;

    const dispose = () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(microId);
      syn.removeEventListener('voiceschanged', onVoices);
      this.pendingBrowserSpeakDispose = null;
    };

    const onVoices = () => {
      if (spoken) return;
      dispose();
      this.browserVoicesCatalogReady = true;
      start();
    };

    syn.addEventListener('voiceschanged', onVoices);
    this.pendingBrowserSpeakDispose = dispose;

    timeoutId = window.setTimeout(() => {
      if (spoken) return;
      dispose();
      this.browserVoicesCatalogReady = true;
      start();
    }, VOICE_CATALOG_DEFER_MS);

    microId = window.setTimeout(() => {
      if (spoken || !syn.getVoices().length) return;
      dispose();
      this.browserVoicesCatalogReady = true;
      start();
    }, 0);
  }

  cancel(): void {
    this.invalidatePendingSpeak('explicit_cancel');
    if (this.isSupported()) {
      try {
        const syn = window.speechSynthesis;
        queueMicrotask(() => {
          try {
            syn.cancel();
          } catch {
            /* ignore */
          }
        });
      } catch {
        /* ignore */
      }
    }
  }

  pause(): void {
    if (!this.isSupported()) return;
    try {
      window.speechSynthesis.pause();
      this.state.next({
        ...this.state.value,
        speaking: false,
        paused: true,
      });
    } catch {
      /* ignore */
    }
  }

  resume(): void {
    if (!this.isSupported()) return;
    try {
      window.speechSynthesis.resume();
      this.state.next({
        ...this.state.value,
        speaking: true,
        paused: false,
      });
    } catch {
      /* ignore */
    }
  }

  private devLog(event: string, data?: Record<string, unknown>): void {
    if (environment.production) return;
    console.info(`[voice-tts] ${event}`, data ?? '');
  }

  ngOnDestroy(): void {
    this.invalidatePendingSpeak('destroy');
    if (this.voicesListener && typeof window !== 'undefined') {
      window.speechSynthesis.removeEventListener(
        'voiceschanged',
        this.voicesListener
      );
    }
  }
}

